import {Button, Hotkey, IconButton} from '@wandb/components';
import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
import {useCallback, useLayoutEffect, useMemo, useRef} from 'react';
import {Popup} from 'semantic-ui-react';

import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import type * as FilterTypes from '../../../util/filterTypes';
import {
  activeFilterCountFromStored,
  type FilterV2Root,
  getFiltersForUI,
  isFilterV2Group,
  type StoredFilter,
  upsertToggleFilter,
} from '../../../util/filterV2';
import type * as RunTypes from '../../../util/runTypes';
import {maybePluralize} from '../../../util/uihelpers';
import {useTrackFilterWithLocation} from '../../Filters/filterEvents';
import {type FilterKeySelectorCreatorProps} from '../../Filters/FilterKeySelector';
import FilterListV2 from '../../Filters/FilterListV2';
import {type FilterValueSelectorCreatorProps} from '../../Filters/FilterValueSelector';
import {useRunSelectorAnalyticsLocation} from '../RunSelectorAnalyticsLocationContext';

interface WBTableActionFilterButtonProps {
  className?: string;
  compact?: boolean;
  notificationOpen?: boolean;
  filtersOpen: boolean;
  filters: StoredFilter;
  extraFilterCount?: number;
  onClick?(): void;
}

export const WBTableActionFilterButton = ({
  compact,
  filters,
  extraFilterCount,
  filtersOpen,
  notificationOpen,
  onClick,
  className,
}: WBTableActionFilterButtonProps) => {
  const analyticsLocation = useRunSelectorAnalyticsLocation();
  const filterCount =
    activeFilterCountFromStored(filters) + (extraFilterCount ?? 0);
  const buttonText =
    filterCount > 0 ? maybePluralize(filterCount, 'filter') : 'Filter';

  const isActive = filtersOpen || filterCount > 0;
  const onPress = () => {
    onClick?.();
    window.analytics?.track('Filter dropdown clicked', {
      location: analyticsLocation,
    });
  };

  return (
    <Popup
      open={notificationOpen}
      position="bottom left"
      basic
      pinned
      style={{
        top: 24,
        marginLeft: 1,
        padding: '4px 8px',
        cursor: 'pointer',
      }}
      inverted
      on="click"
      content="New filter applied"
      trigger={
        compact ? (
          <IconButton
            tooltip={{children: 'Filter'}}
            aria-label="Filter"
            isActive={isActive}
            variant="secondary"
            icon="filter-alt"
            data-test="filter-button"
            onPress={onPress}
          />
        ) : (
          <Button
            isActive={isActive}
            variant="secondary"
            startIcon="filter-alt"
            data-test="filter-button"
            aria-label={buttonText}
            onPress={onPress}>
            {buttonText}
          </Button>
        )
      }
    />
  );
};

interface WBTableActionFilterPickerProps {
  filters: StoredFilter;

  defaultToggleFilters?: FilterTypes.DefaultToggleFilter[];

  overflowFunc?: () => void;

  hideManuallyHidden?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };

  headerContent?: React.ReactNode;

  setFilters(filters: StoredFilter): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;

  filterValueSelector(
    props: FilterValueSelectorCreatorProps<RunTypes.Key>
  ): React.ReactNode;
}

export const WBTableActionFilterPicker = (
  props: WBTableActionFilterPickerProps
) => {
  const {filters, overflowFunc} = props;

  // Filter dropdown is a little jank in the custom charts editor.
  // We need to remount the dropdown when it juts out of the viewport,
  // so that we bring it back into view.
  const filterRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (filterRef.current == null || overflowFunc == null) {
        return;
      }
      const {top, left, right, bottom} =
        filterRef.current.getBoundingClientRect();
      const {innerWidth: viewportWidth, innerHeight: viewportHeight} = window;
      if (
        right > viewportWidth ||
        bottom > viewportHeight ||
        left < 0 ||
        top < 0
      ) {
        overflowFunc();
      }
    });
    return () => cancelAnimationFrame(raf);
  });

  return (
    <div className="filter-popup" ref={filterRef}>
      <FilterPickerContent {...props} />
    </div>
  );
};

const FilterPickerContent = (props: WBTableActionFilterPickerProps) => {
  const {filters, defaultToggleFilters, hideManuallyHidden} = props;
  const {trackGroupCreated, trackORToggled} = useTrackFilterWithLocation();
  const v2Root = useMemo(() => getFiltersForUI(filters), [filters]);

  const handleChange = useCallback(
    (newRoot: FilterV2Root) => {
      props.setFilters(newRoot);
    },
    [props]
  );

  const handleToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) => {
      const updated = upsertToggleFilter(v2Root, toggleFilter);
      props.setFilters(updated);
    },
    [v2Root, props]
  );

  const hasEnabledToggle = useCallback(
    (toggleFilter: FilterTypes.IndividualFilter<RunTypes.Key>) =>
      v2Root.filters.some(
        item =>
          !isFilterV2Group(item) &&
          !item.disabled &&
          item.key.section === toggleFilter.key.section &&
          item.key.name === toggleFilter.key.name &&
          item.op === toggleFilter.op &&
          JSON.stringify(item.value) === JSON.stringify(toggleFilter.value)
      ),
    [v2Root]
  );

  return (
    <>
      {props.headerContent}
      <FilterListV2
        root={v2Root}
        onChange={handleChange}
        onGroupCreated={trackGroupCreated}
        onORToggled={trackORToggled}
        filterKeySelector={props.filterKeySelector}
        filterValueSelector={
          props.filterValueSelector as unknown as (
            p: FilterValueSelectorCreatorProps<FilterTypes.FilterKey>
          ) => React.ReactNode
        }
      />
      {(defaultToggleFilters || hideManuallyHidden) && (
        <WBTableActionFilterPickerDefaultToggles
          defaultToggleFilters={defaultToggleFilters ?? []}
          onToggle={handleToggle}
          hasEnabled={hasEnabledToggle}
          hideManuallyHidden={hideManuallyHidden}
          showDivider
        />
      )}
    </>
  );
};

const WBTableActionFilterPickerDefaultToggles = ({
  defaultToggleFilters,
  onToggle,
  hasEnabled,
  hideManuallyHidden,
  showDivider,
}: {
  defaultToggleFilters: FilterTypes.DefaultToggleFilter[];
  onToggle: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => void;
  hasEnabled: (filter: FilterTypes.IndividualFilter<RunTypes.Key>) => boolean;
  hideManuallyHidden?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };
  showDivider?: boolean;
}) => {
  const isInReport = useIsInLoadedReportContext();
  return (
    <Tailwind>
      {showDivider && <hr className="my-16 text-moon-250" />}
      <div className="flex flex-col gap-8">
        {defaultToggleFilters.map((defaultFilter, i) => (
          // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
          <div key={i} className="flex justify-between">
            <span>{defaultFilter.label}</span>
            <Switch.Root
              size="medium"
              checked={hasEnabled(defaultFilter.filter)}
              onCheckedChange={() => onToggle(defaultFilter.filter)}>
              <Switch.Thumb
                size="medium"
                checked={hasEnabled(defaultFilter.filter)}
              />
            </Switch.Root>
          </div>
        ))}
        {hideManuallyHidden && (
          <div className="flex items-center justify-between">
            <span>List only visible runs</span>
            <div className="flex items-center gap-8">
              {!isInReport && (
                // TODO: remove once --reverse-app-filter
                <span className="night-aware">
                  <Hotkey
                    keys={['Alt', 'KeyV']}
                    name="List only visible runs"
                  />
                </span>
              )}
              <Switch.Root
                size="medium"
                checked={hideManuallyHidden.checked}
                onCheckedChange={hideManuallyHidden.onChange}>
                <Switch.Thumb
                  size="medium"
                  checked={hideManuallyHidden.checked}
                />
              </Switch.Root>
            </div>
          </div>
        )}
      </div>
    </Tailwind>
  );
};

interface WBTableActionFilterProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trigger: (isOpen: boolean) => React.ReactNode;
  content: React.ReactNode;
  preventOverflow?: boolean;
}

export const WBTableActionFilter = (props: WBTableActionFilterProps) => {
  return (
    <Popup
      basic
      className="wb-table-action-popup"
      // Portal-rendered, so it doesn't inherit the run table's allow; filter
      // keys/values shown here are already exposed in the table itself.
      data-dd-privacy={DD_PRIVACY_ALLOW}
      style={{
        maxWidth: 'none',
      }}
      on="click"
      position="bottom left"
      open={props.open}
      onOpen={() => props.setOpen(true)}
      onClose={() => props.setOpen(false)}
      trigger={props.trigger(props.open)}
      content={
        <Tailwind>
          <div className="flex justify-between mb-10">
            <span className="text-lg font-semibold">Filters</span>
            <IconButton
              size="md"
              variant="ghost"
              icon="close"
              aria-label="Close filters"
              onPress={() => props.setOpen(false)}
            />
          </div>
          {props.content}
        </Tailwind>
      }
      popperModifiers={{
        preventOverflow: {enabled: props.preventOverflow ?? false},
        flip: {enabled: false},
      }}
    />
  );
};
